// =============================================================================
// ESPOLIDAR WEB ADMIN - RESELLER DETAIL PAGE
// =============================================================================

import { useParams, Link } from 'react-router-dom'
import {
  useReseller,
  useUpdateReseller,
  useResellerDevices,
  useResellerUsers,
  useAssignDevice,
  useUnassignDevice,
} from '../hooks/useResellers'
import { useDevices } from '../hooks/useDevices'
import {
  Building2,
  ArrowLeft,
  Save,
  Cpu,
  Users,
  Plus,
  Trash2,
  Wifi,
  WifiOff,
} from 'lucide-react'
import LoadingSpinner from '../components/LoadingSpinner'
import { useState, useEffect } from 'react'
import type { ResellerUpdate } from '../types'

export default function ResellerDetailPage() {
  const { id } = useParams<{ id: string }>()
  const resellerId = parseInt(id || '0')

  const { data: reseller, isLoading } = useReseller(resellerId)
  const { data: resellerDevices } = useResellerDevices(resellerId)
  const { data: resellerUsers } = useResellerUsers(resellerId)
  const { data: allDevices } = useDevices()

  const updateReseller = useUpdateReseller()
  const assignDevice = useAssignDevice()
  const unassignDevice = useUnassignDevice()

  const [formData, setFormData] = useState<ResellerUpdate>({
    name: '',
    email: '',
    phone: '',
    is_active: true,
  })

  const [showAssignDevice, setShowAssignDevice] = useState(false)

  // Dispositivi non assegnati a nessun reseller
  const unassignedDevices = allDevices?.filter((d) => !d.reseller_id)

  useEffect(() => {
    if (reseller) {
      setFormData({
        name: reseller.name,
        email: reseller.email || '',
        phone: reseller.phone || '',
        is_active: reseller.is_active,
      })
    }
  }, [reseller])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    updateReseller.mutate({ id: resellerId, data: formData })
  }

  const handleAssignDevice = (deviceId: string) => {
    assignDevice.mutate({ resellerId, deviceId }, {
      onSuccess: () => setShowAssignDevice(false),
    })
  }

  const handleUnassignDevice = (deviceId: string) => {
    if (confirm('Rimuovere questo dispositivo dal reseller?')) {
      unassignDevice.mutate({ resellerId, deviceId })
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!reseller) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Reseller non trovato</p>
        <Link to="/resellers" className="text-primary-600 hover:underline mt-2 block">
          Torna alla lista
        </Link>
      </div>
    )
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Link
          to="/resellers"
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft size={24} className="text-gray-600" />
        </Link>
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center">
            <Building2 size={24} className="text-primary-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{reseller.name}</h1>
            <p className="text-gray-500 font-mono">{reseller.code}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Form Edit */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Dati Reseller</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="input w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="input w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Telefono
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="input w-full"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-4 h-4 text-primary-600 rounded"
                />
                <label htmlFor="is_active" className="text-sm text-gray-700">
                  Reseller attivo
                </label>
              </div>
              <button
                type="submit"
                className="btn btn-primary w-full flex items-center justify-center gap-2"
                disabled={updateReseller.isPending}
              >
                <Save size={18} />
                {updateReseller.isPending ? 'Salvataggio...' : 'Salva'}
              </button>
            </form>
          </div>
        </div>

        {/* Dispositivi */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Cpu size={20} className="text-gray-400" />
                Dispositivi ({resellerDevices?.length || 0})
              </h2>
              <button
                onClick={() => setShowAssignDevice(true)}
                className="btn btn-secondary btn-sm flex items-center gap-1"
              >
                <Plus size={16} />
                Assegna
              </button>
            </div>

            {/* Assign Device Modal */}
            {showAssignDevice && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">
                    Assegna Dispositivo
                  </h3>
                  {unassignedDevices && unassignedDevices.length > 0 ? (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {unassignedDevices.map((device) => (
                        <button
                          key={device.id}
                          onClick={() => handleAssignDevice(device.device_id)}
                          className="w-full p-3 text-left border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors"
                          disabled={assignDevice.isPending}
                        >
                          <div className="flex items-center gap-3">
                            <div
                              className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                                device.status === 'online'
                                  ? 'bg-green-100 text-green-600'
                                  : 'bg-gray-100 text-gray-400'
                              }`}
                            >
                              <Cpu size={16} />
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{device.name}</p>
                              <p className="text-xs text-gray-500 font-mono">
                                {device.device_id}
                              </p>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-4">
                      Nessun dispositivo disponibile da assegnare
                    </p>
                  )}
                  <div className="flex justify-end mt-4">
                    <button
                      onClick={() => setShowAssignDevice(false)}
                      className="btn btn-secondary"
                    >
                      Chiudi
                    </button>
                  </div>
                </div>
              </div>
            )}

            {resellerDevices && resellerDevices.length > 0 ? (
              <div className="space-y-2">
                {resellerDevices.map((device: any) => (
                  <div
                    key={device.id}
                    className="flex items-center justify-between p-3 border border-gray-100 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          device.status === 'online'
                            ? 'bg-green-100 text-green-600'
                            : 'bg-gray-100 text-gray-400'
                        }`}
                      >
                        <Cpu size={20} />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{device.name}</p>
                        <p className="text-sm text-gray-500 font-mono">{device.device_id}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                          device.status === 'online'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {device.status === 'online' ? (
                          <Wifi size={12} />
                        ) : (
                          <WifiOff size={12} />
                        )}
                        {device.status}
                      </span>
                      <button
                        onClick={() => handleUnassignDevice(device.device_id)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        disabled={unassignDevice.isPending}
                        title="Rimuovi"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">
                Nessun dispositivo assegnato a questo reseller
              </p>
            )}
          </div>

          {/* Utenti */}
          <div className="bg-white rounded-xl shadow-sm p-6 mt-6">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
              <Users size={20} className="text-gray-400" />
              Utenti ({resellerUsers?.length || 0})
            </h2>
            {resellerUsers && resellerUsers.length > 0 ? (
              <div className="space-y-2">
                {resellerUsers.map((user: any) => (
                  <div
                    key={user.id}
                    className="flex items-center justify-between p-3 border border-gray-100 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                        <span className="text-primary-600 font-medium">
                          {user.username.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{user.username}</p>
                        <p className="text-sm text-gray-500">{user.email}</p>
                      </div>
                    </div>
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        user.role === 'admin'
                          ? 'bg-purple-100 text-purple-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {user.role}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">
                Nessun utente assegnato a questo reseller
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
